import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Plus, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

export default function BusinessRequestsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFilings = async () => {
      try {
        setLoading(true);
        const backendUrl = getBackendUrl();
        const token = localStorage.getItem('access_token');
        
        const response = await axios.get(`${backendUrl}/api/tax-filings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const allFilings = response.data || [];
        // Filter for business filings only and sort by date
        const businessFilings = allFilings
          .filter(f => f.filing_type === 'business')
          .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
        
        setFilings(businessFilings);
      } catch (error) {
        console.error('Error fetching business filings:', error);
        toast.error('Failed to load business filings');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchFilings();
    }
  }, [user]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'DRAFT': { label: 'Draft', className: 'bg-gray-500' },
      'SUBMITTED': { label: 'Submitted', className: 'bg-blue-500' },
      'NEED_INFO': { label: 'Need Info', className: 'bg-yellow-500' },
      'READY_FOR_REVIEW': { label: 'Ready for Review', className: 'bg-green-500' },
      'SUBMITTED_TO_IRS': { label: 'Submitted to IRS', className: 'bg-green-600' },
    };
    return statusConfig[status] || { label: status, className: 'bg-gray-500' };
  };

  const handleFilingClick = (filing) => {
    const isEditable = filing.status === 'DRAFT' || filing.status === 'NEED_INFO';
    if (isEditable) {
      navigate(`/business/filing/start?id=${filing.id}`);
    } else {
      navigate(`/business/filing/start?id=${filing.id}&mode=view`);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <DashboardLayout role={user.role} title="My Business Tax Filings" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: 'Business Dashboard', href: '/business/dashboard' },
          { label: 'My Requests' }
        ]} />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">My Business Tax Filing Requests</h2>
            <p className="text-muted-foreground mt-1">
              View and manage all your business tax filing requests
            </p>
          </div>
          <Button onClick={() => navigate('/business/filing/start?new=true')}>
            <Plus className="mr-2 h-4 w-4" />
            New Business Filing
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : filings.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Briefcase className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Business Filings Yet</h3>
                <p className="text-muted-foreground mb-4">
                  You haven't created any business tax filings yet.
                </p>
                <Button onClick={() => navigate('/business/filing/start?new=true')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Start New Filing
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filings.map((filing) => {
              const statusBadge = getStatusBadge(filing.status);
              const businessName = filing.personal_info?.business_name || 'Unnamed Business';
              
              return (
                <Card key={filing.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="rounded-lg bg-primary/10 p-2">
                            <Briefcase className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{businessName}</h3>
                            <p className="text-sm text-muted-foreground">
                              Tax Year {filing.tax_year}
                            </p>
                          </div>
                        </div>
                        
                        <div className="ml-14 grid gap-3 md:grid-cols-3 mt-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Status</p>
                            <Badge className={`${statusBadge.className} text-white mt-1`}>
                              {statusBadge.label}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Submitted</p>
                            <p className="text-sm font-medium mt-1">
                              {filing.created_at ? new Date(filing.created_at).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Last Updated</p>
                            <p className="text-sm font-medium mt-1">
                              {filing.updated_at ? new Date(filing.updated_at).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {filing.status_message && (
                          <div className="ml-14 mt-4 p-3 bg-muted rounded-lg">
                            <p className="text-sm">{filing.status_message}</p>
                          </div>
                        )}
                      </div>

                      <Button 
                        variant="outline"
                        onClick={() => handleFilingClick(filing)}
                      >
                        View Details
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
